import { ImageIcon } from "lucide-react";
import { Section, SectionHeading, PageHero } from "../components/site/section";
import { PageCta } from "../components/site/page-cta";
import { asset } from "../lib/assets";

const batteryImage = { url: asset("nyc-historical-battery.png") };
const villageImage = { url: asset("nyc-historical-village.png") };
const zoningMap = { url: asset("zoning-map-new.png") };
const sanbornMap = { url: asset("sanborn-map.png") };
const recordBuildingImages = { url: asset("record-building-images.png") };
const recordStreetMap = { url: asset("record-street-map.png") };
const recordTaxMap = { url: asset("record-tax-map-new.png") };
const recordFema = { url: asset("record-fema.png") };
const recordCo = { url: asset("co.png") };
const recordFdny = { url: asset("fdny_building.png") };
const recordICards = { url: asset("image_cards.png") };
const recordLpc = { url: asset("lpc_des_2.png") };

const records = [
    { title: "Building Images", body: "Street-level, aerial, and facade photography spanning current, 1980s, and 1940s collections.", image: recordBuildingImages.url, imageAlt: "Black-and-white street-level photograph of row buildings with awnings and a vintage car at the curb" },
    {
        title: "1920s Street View",
        body: "Historical aerial photography of the 1920s streetscape surrounding the property.",
        image: batteryImage.url,
        imageAlt: "Historical aerial view of the Financial District and The Battery in Manhattan",
    },
    {
        title: "1950s Street View",
        body: "Historical aerial photography of the 1950s streetscape surrounding the property.",
        image: villageImage.url,
        imageAlt: "Historical aerial view of West Village, Greenwich Village and Washington Square Park",
    },
    {
        title: "Sanborn Map",
        body: "Digitized fire-insurance maps showing building footprints, uses, and hazards.",
        image: sanbornMap.url,
        imageAlt: "Sanborn fire-insurance map of Manhattan blocks near East 28th Street and Madison Avenue",
    },
    { title: "Street Map", body: "City street map context for the property and the surrounding blocks.", image: recordStreetMap.url, imageAlt: "Aerial street map view of a Manhattan block near West 50th Street and Eighth Avenue" },
    {
        title: "Zoning Map",
        body: "Official zoning district boundaries, use groups, and special district regulations.",
        image: zoningMap.url,
        imageAlt: "New York City zoning map showing Midtown Manhattan district boundaries around Central Park",
    },
    { title: "Tax Map", body: "City tax map lot and block outlines tied to ownership and assessment records.", image: recordTaxMap.url, imageAlt: "City tax map showing lots and block numbers near West 16th Street and the Avenue of the Americas" },
    { title: "FEMA", body: "Flood zone and flood insurance rate map data for the site.", image: recordFema.url, imageAlt: "FEMA flood insurance rate map showing Zone AE flood boundaries along a Manhattan shoreline" },
    { title: "Certificate of Occupancy", body: "Current and historical certificates of occupancy showing legal use and occupancy.", image: recordCo.url, imageAlt: "Historic Certificate of Occupancy No. 17502 issued by the Bureau of Buildings, Borough of Manhattan, 1931" },
    { title: "FDNY Building Information Profile", body: "Fire department building notes, hazards, and response information.", image: recordFdny.url, imageAlt: "FDNY Building Information Profile report listing block, lot, and BIN details with sprinkler and standpipe data" },
    { title: "Historical Image Cards (I-Cards)", body: "Historical records showing a building’s past occupancy, layout, and documented legal use.", image: recordICards.url, imageAlt: "Historical Division of Housing Classification card filled out by hand with building classification details" },
    { title: "LPC Designation", body: "Landmark status, historic district designation, and related restrictions.", image: recordLpc.url, imageAlt: "Landmarks Preservation Commission designation list for the Empire State Building, 1981" },
];
function HistoricalData() {
    return (<>
      <PageHero title="The full record of a building, not just what's open today." description="Coverage depends on the agency, but most datasets extend more than a decade and several reach back to the 1990s."/>
      <Section>
        <SectionHeading title="Record types"/>
        <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {records.map((r) => (<RecordCard key={r.title} record={r}/>))}
        </div>
      </Section>
      <PageCta />
    </>);
}
function RecordCard({ record: r }) {
    return (<div className="group [perspective:1400px]">
      <div className="relative aspect-square w-full transform-3d transition-transform duration-500 ease-out group-hover:rotate-y-180">
        {/* Front — square image (or placeholder until its photo is added) */}
        <div className="absolute inset-0 overflow-hidden rounded-xl border border-border bg-card shadow-card backface-hidden">
          {r.image ? (<img src={r.image} alt={r.imageAlt ?? r.title} loading="lazy" className="h-full w-full object-cover"/>) : (<div className="flex h-full w-full flex-col items-center justify-center gap-4 bg-ink px-6 text-center">
              <ImageIcon className="h-9 w-9 text-signal" strokeWidth={1.5}/>
              <p className="font-display text-base font-semibold text-ink-foreground">{r.title}</p>
            </div>)}
        </div>

        {/* Back — the record description */}
        <div className="absolute inset-0 flex rotate-y-180 flex-col justify-center rounded-xl border border-border bg-ink p-6 text-center backface-hidden">
          <h2 className="font-display text-lg font-semibold text-signal">{r.title}</h2>
          <p className="mt-3 text-sm leading-relaxed text-ink-foreground">{r.body}</p>
        </div>
      </div>
    </div>);
}

export default HistoricalData;
